import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import api from "../api/axios";
import { useLanguage } from "../context/LanguageContext";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle, submitting, success
    const { t } = useLanguage();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;

        setStatus("submitting");

        try {
            await api.post("/auth/forgot-password", { email, origin: window.location.origin });
            setStatus("success");
            // eslint-disable-next-line no-unused-vars
        } catch (err) {
            // We show success even on error to prevent email enumeration
            setStatus("success");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
                >
                    {status === "success" ? (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth_forgot_check_mail')}</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                {t('auth_forgot_sent_to')} <span className="font-semibold text-gray-900 dark:text-white">{email}</span>, {t('auth_forgot_instructions')}
                            </p>
                            <Link
                                to="/login"
                                className="btn-primary w-full block text-center mt-6"
                            >
                                {t('auth_forgot_back')}
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth_forgot_title')}</h2>
                                <p className="text-gray-500 dark:text-gray-400">
                                    {t('auth_forgot_desc')}
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('auth_forgot_email_label')}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                            <Mail className="w-5 h-5" />
                                        </div>
                                        <input
                                            id="email"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full px-4 py-3 pl-12 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-brand-blue focus:outline-none transition-all"
                                            placeholder={t('auth_forgot_email_placeholder')}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === "submitting"}
                                    className="btn-primary w-full flex items-center justify-center"
                                >
                                    {status === "submitting" ? (
                                        <span className="flex items-center gap-2">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t('err_starting')}
                                        </span>
                                    ) : t('auth_forgot_cta')}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    {t('auth_forgot_back')}
                                </Link>
                            </div>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
